/**
 * Die Kurzfassung der Neuigkeiten OHNE KI — rein, für Server (server/neuigkeiten-kurz.ts) und Karte (frontend/lib/was-ist-neu.ts)
 * gemeinsam: die Karte zeigt sofort diesen Rückfall und tauscht die Punkte aus, wenn die KI-Fassung da ist. (18.10.2026)
 */

/** So viele Punkte hat die Kurzfassung höchstens. */
export const KURZ_PUNKTE = 5;
/** So lang ist ein Punkt höchstens. */
export const KURZ_ZEICHEN = 110;

/** Eine Zeile für die Kurzfassung: ohne Ticketnummern, Adressen, Emojis; Leerraum zusammengezogen. Rein. */
export function saeubere(text: string): string {
  return String(text ?? "")
    .replace(/\b(?:https?:\/\/|www\.)\S+/gi, "")
    .replace(/\(?\b(?:Tickets?|Ticket-Nr\.?)\s*#?\d{3,5}(?:\s*(?:,|\/|&|und|sowie)\s*#?\d{3,5})*\)?/gi, "")
    .replace(/#\d{3,5}\b/g, "")
    .replace(/\p{Extended_Pictographic}/gu, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
}

/** Auf höchstens `max` Zeichen an der Wortgrenze, mit „…“ am Ende. Rein. */
export function kuerzeAnWortgrenze(text: string, max: number = KURZ_ZEICHEN): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const kopf = t.slice(0, max - 1);
  const leer = kopf.lastIndexOf(" ");
  // Ein einzelnes riesiges Wort wird hart gekappt.
  const ende = leer > max / 2 ? kopf.slice(0, leer) : kopf;
  return `${ende.replace(/[\s,;:–—-]+$/u, "")}…`;
}

export interface Rueckfall {
  /** Alle Punkte, wie die Karte sie zeigt (bei mehr als KURZ_PUNKTE Zeilen mit „… und N weitere“ als letztem). */
  punkte: string[];
  /** Nur die Punkte aus echten Zeilen, ohne den „… und N weitere“-Rest — das spricht Vespera. */
  ohneRest: string[];
  /** Positionen (in `zeilen`) der Zeilen, die VOLLSTÄNDIG genannt sind: vorn, nicht gekürzt. */
  gedeckt: number[];
  /** Positionen der genannten Zeilen, die GEKÜRZT sind und deren Details nicht auf der Karte stehen — sie gelten (noch) nicht als gehört. */
  nurGekuerzt: number[];
}

/**
 * Der Rückfall: die ersten Zeilen, je auf KURZ_ZEICHEN an der Wortgrenze gekürzt. Sind es mehr als KURZ_PUNKTE, steht der letzte Platz
 * für „… und N weitere“ — es bleiben höchstens KURZ_PUNKTE Punkte. `zeilen` sind schon gesäubert und nicht leer. Eine gekürzte Zeile
 * gilt als gedeckt, wenn ihre Details auf der Karte erreichbar sind (`aufKarte[i]`, Standard: ja) — sonst steht sie in `nurGekuerzt`. Rein.
 */
export function rueckfallMit(zeilen: readonly string[], aufKarte: readonly boolean[] = []): Rueckfall {
  const viele = zeilen.length > KURZ_PUNKTE;
  const genannt = viele ? zeilen.slice(0, KURZ_PUNKTE - 1) : zeilen;
  const ohneRest = genannt.map((z) => kuerzeAnWortgrenze(z));
  const ganz = (i: number): boolean => ohneRest[i] === genannt[i];
  const gedeckt = ohneRest.flatMap((_, i) => (ganz(i) || aufKarte[i] !== false ? [i] : []));
  const nurGekuerzt = ohneRest.flatMap((_, i) => (gedeckt.includes(i) ? [] : [i]));
  return { punkte: viele ? [...ohneRest, `… und ${zeilen.length - genannt.length} weitere`] : ohneRest, ohneRest, gedeckt, nurGekuerzt };
}

/** Der Rückfall aus rohen Zeilen. Rein. */
export function rueckfallKurz(zeilen: readonly string[]): string[] {
  return rueckfallMit(zeilen.map(saeubere).filter(Boolean)).punkte;
}
